import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved -- resolver doesn't read motion-plus's exports map
import { Ticker } from 'motion-plus/react';
import { useReducedMotion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import generalHelpers from '../../../services/generalHelpers';

// Build a {pathname, search} location for <Link> that carries stored UTMs, so
// campaign attribution survives the hop to the reviews page (repo-wide rule).
const withUTMs = (path) => {
  const [pathname, search] = generalHelpers.appendStoredUTMs(path).split('?');
  return { pathname, search: search ? `?${search}` : '' };
};

// Simple, on-brand reviews marquee that replaces Yotpo's embedded widget below
// the FINAL section. Shows the most recent reviews, fetched straight from
// Yotpo's public widget JSON API (same endpoint the widget uses — no auth,
// CORS-enabled, CDN-cached). The infinite auto-scroll loop is Motion+'s Ticker.
// Self-contained fetch. The app GUID matches services/Yotpo.js.
const YOTPO_APP_KEY = 'MtI6WMUc1SMDm8DW3ZmRDSTA4TUsLomtzi81ZY7C';
const COUNT = 20; // how many cards to show
const CANDIDATES = 100; // pool fetched from the API (most-recent)
const CLAMP_WORDS = 40; // reviews longer than this get a Show more/less toggle
const BASE_VELOCITY = 30; // gentle default drift (px/s)
const SCRUB_VELOCITY = 840; // fast-forward / rewind speed while an arrow is held
// Minimum time the scrub speed is held after a press, so a quick TAP (not just
// a long press) still produces a visible scoot — otherwise a click bumps the
// speed for ~1 frame and looks like the arrow does nothing.
const MIN_SCRUB_MS = 400;

const Star = ({ className = '' }) => (
  <svg className={className} viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M12 2l3 7 7 .6-5.3 4.7L18 22l-6-3.7L6 22l1.3-7.7L2 9.6 9 9z' />
  </svg>
);

// Five stars, filled up to the review's rounded score; the rest render faded.
const RatingStars = ({ score = 0 }) => {
  const filled = Math.round(score);
  return (
    <span className='tr-stars' aria-label={`${score} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        // eslint-disable-next-line react/no-array-index-key
        <Star key={n} className={n > filled ? 'is-empty' : ''} />
      ))}
    </span>
  );
};

// "7/22/25" — matches the reference attribution line.
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};

// Fetch the most-recent reviews (all ratings). Returns an array (possibly empty)
// or null on failure so the caller can fall back to the embedded widget.
const fetchTopReviews = async (productId) => {
  if (!productId) return null;
  try {
    const params = new URLSearchParams();
    params.set('per_page', String(CANDIDATES));
    params.set('page', '1');
    params.set('sort', 'date');
    params.set('direction', 'desc');
    const res = await fetch(
      `https://api-cdn.yotpo.com/v1/widget/${YOTPO_APP_KEY}/products/${productId}/reviews.json?${params}`,
    );
    if (!res.ok) return null;
    const r = (await res.json())?.response;
    const reviews = Array.isArray(r?.reviews) ? r.reviews : [];
    return reviews
      .slice(0, COUNT)
      .map((rv) => ({
        id: rv.id,
        score: rv.score || 0,
        title: rv.title || '',
        content: rv.content || '',
        name: rv.user?.display_name || 'Verified Buyer',
        createdAt: rv.created_at || null,
        verified: Boolean(rv.verified_buyer),
      }));
  } catch (err) {
    return null;
  }
};

const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const words = (review.content || '').trim().split(/\s+/).filter(Boolean);
  const isLong = words.length > CLAMP_WORDS;
  const body =
    expanded || !isLong
      ? review.content
      : `${words.slice(0, CLAMP_WORDS).join(' ')}…`;

  return (
    <div className={`tr-card${expanded ? ' is-expanded' : ''}`}>
      <RatingStars score={review.score} />
      {review.content && (
        <p className='tr-body'>
          {body}
          {isLong && (
            <button
              type='button'
              className='tr-more'
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'show less' : 'show more'}
            </button>
          )}
        </p>
      )}
      <span className='tr-meta'>
        <span className='tr-name'>{review.name}</span>
        {formatDate(review.createdAt) && `, ${formatDate(review.createdAt)}`}
      </span>
    </div>
  );
};

// Props:
//  - productId  Yotpo product id (subscriptionPlan.yotpoSku || sku)
//  - fallback   rendered when the API fails / returns no reviews (the embedded
//               Yotpo <Reviews> widget), so the section is never empty
const TopReviews = ({ productId, fallback = null }) => {
  const [reviews, setReviews] = useState(null); // null = loading
  // Scroll speed in px/s, driven straight into the Ticker. Gentle drift by
  // default; holding an arrow fast-forwards (+) or rewinds (−); touching the
  // marquee itself pauses it (mobile — no hover). Desktop pauses via hoverFactor.
  const [velocity, setVelocity] = useState(BASE_VELOCITY);
  const scrubbing = useRef(false); // true between a press and its release
  const scrubStart = useRef(0); // timestamp of the current press
  const scrubTimer = useRef(null); // pending "ease back to drift" timeout
  // The Motion+ Ticker disables its own animation loop under
  // prefers-reduced-motion AND clips its overflow, which leaves those users a
  // frozen, unscrollable strip (auto-scroll dead, arrows dead — they only feed
  // the disabled loop). When reduced motion is requested we render a real
  // horizontally-scrollable row instead, with the arrows wired to scrollBy.
  const reducedMotion = useReducedMotion();
  const scrollerRef = useRef(null); // fallback scroll container (reduced-motion)
  // Hide the scroller's arrows at each end (reduced-motion branch only). Starts
  // false/false because we park the scroller at the middle — both ends reachable.
  const [atStart, setAtStart] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchTopReviews(productId).then((data) => {
      if (!cancelled) setReviews(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Clear any pending scrub-release timer on unmount.
  useEffect(
    () => () => {
      if (scrubTimer.current) clearTimeout(scrubTimer.current);
    },
    [],
  );

  // Reduced-motion native scroller: park it at the middle on mount so there's
  // visible runway in both directions (and the middle reviews aren't hidden off
  // to the right). Finite scroll — no looping, which felt too jumpy. Track the
  // ends so the arrows hide once there's nothing more to scroll that way.
  useEffect(() => {
    if (!reducedMotion) return undefined;
    const el = scrollerRef.current;
    if (!el) return undefined;
    const update = () => {
      // 1px slack absorbs sub-pixel rounding so the end arrows reliably hide.
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [reducedMotion, reviews]);

  if (reviews === null) return null; // brief loading — render nothing
  if (reviews.length === 0) return fallback;

  const cards = reviews.map((rv) => <ReviewCard key={rv.id} review={rv} />);

  // Reduced-motion fallback: no marquee (the Ticker won't animate anyway), just
  // a native-scroll row (finite, parked at the middle on mount). Free swipe plus
  // arrows that jump ~a viewport of cards; behavior 'auto' (not 'smooth')
  // because the user asked for less motion.
  if (reducedMotion) {
    const page = (dir) => () => {
      const el = scrollerRef.current;
      if (!el) return;
      const amount = Math.max(316, Math.round(el.clientWidth * 0.8));
      el.scrollBy({ left: dir * amount, behavior: 'auto' });
    };
    return (
      <section className='section white tr-section' id='topReviews'>
        <div className='tr-carousel'>
          <button
            type='button'
            className={`tr-nav prev${atStart ? ' is-hidden' : ''}`}
            aria-label='Previous reviews'
            aria-hidden={atStart}
            tabIndex={atStart ? -1 : 0}
            onClick={page(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div className='tr-ticker tr-scroller' ref={scrollerRef}>
            {cards}
          </div>

          <button
            type='button'
            className={`tr-nav next${atEnd ? ' is-hidden' : ''}`}
            aria-label='Next reviews'
            aria-hidden={atEnd}
            tabIndex={atEnd ? -1 : 0}
            onClick={page(1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        <Link to={withUTMs('/reviews')} className='tr-all'>
          See All Reviews
        </Link>
      </section>
    );
  }

  // Press-and-hold an arrow to scrub; a quick tap still scoots because the fast
  // speed is held for at least MIN_SCRUB_MS before easing back to the drift.
  const startScrub = (dir) => {
    if (scrubTimer.current) {
      clearTimeout(scrubTimer.current);
      scrubTimer.current = null;
    }
    scrubbing.current = true;
    scrubStart.current = performance.now();
    setVelocity(dir * SCRUB_VELOCITY);
  };
  // Release (or drag off / blur). Fires from several handlers, so guard against
  // running twice for one press.
  const endScrub = () => {
    if (!scrubbing.current) return;
    scrubbing.current = false;
    const remaining = Math.max(0, MIN_SCRUB_MS - (performance.now() - scrubStart.current));
    if (remaining === 0) {
      setVelocity(BASE_VELOCITY);
      return;
    }
    scrubTimer.current = setTimeout(() => {
      setVelocity(BASE_VELOCITY);
      scrubTimer.current = null;
    }, remaining);
  };
  // Keyboard activation (Enter/Space) on a <button> fires a click with
  // detail === 0. Give those users a single scoot via the same path; mouse and
  // touch clicks (detail >= 1) already go through the pointer handlers, so skip
  // them here to avoid a double burst.
  const keyScoot = (dir) => (e) => {
    if (e.detail !== 0) return;
    startScrub(dir);
    endScrub();
  };

  return (
    <section className='section white tr-section' id='topReviews'>
      {/* Motion+ Ticker — an infinite auto-scrolling marquee (no start/end).
          hoverFactor 0 pauses on hover (desktop); velocity 0 while touched
          pauses on mobile. The arrows scrub speed/direction: tap for a quick
          scoot or press-and-hold to fast-forward / rewind, then it eases back
          to the gentle drift. */}
      <div className='tr-carousel'>
        <button
          type='button'
          className='tr-nav prev'
          aria-label='Rewind reviews'
          onPointerDown={() => startScrub(-1)}
          onPointerUp={endScrub}
          onPointerLeave={endScrub}
          onPointerCancel={endScrub}
          onBlur={endScrub}
          onClick={keyScoot(-1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        <Ticker
          className='tr-ticker'
          axis='x'
          velocity={velocity}
          hoverFactor={0}
          gap={16}
          align='start'
          onTouchStart={() => setVelocity(0)}
          onTouchEnd={() => setVelocity(BASE_VELOCITY)}
          onTouchCancel={() => setVelocity(BASE_VELOCITY)}
          items={cards}
        />

        <button
          type='button'
          className='tr-nav next'
          aria-label='Fast-forward reviews'
          onPointerDown={() => startScrub(1)}
          onPointerUp={endScrub}
          onPointerLeave={endScrub}
          onPointerCancel={endScrub}
          onBlur={endScrub}
          onClick={keyScoot(1)}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <Link to={withUTMs('/reviews')} className='tr-all'>
        See All Reviews
      </Link>
    </section>
  );
};

export default TopReviews;
